import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import amblem from "../../../assets/img/terakki-amblem-beyaz.png";

// @react-pdf'in gömülü fontları Latin-1 ile sınırlı; ş/ğ/İ gibi harfler bozuk
// çıkıyordu. Uygulamanın fontu olan Poppins'i TTF olarak kaydediyoruz.
const POPPINS = "https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/poppins";
const SYMBOLS = "https://cdn.jsdelivr.net/npm/dejavu-fonts-ttf@2.37.3/ttf/DejaVuSans.ttf";
let registered = false;

export function ensureFonts() {
  if (registered) return;
  Font.register({
    family: "Poppins",
    fonts: [
      { src: `${POPPINS}/Poppins-Regular.ttf`, fontWeight: 400 },
      { src: `${POPPINS}/Poppins-SemiBold.ttf`, fontWeight: 600 },
      { src: `${POPPINS}/Poppins-Bold.ttf`, fontWeight: 700 },
    ],
  });
  Font.register({ family: "Symbols", src: SYMBOLS });
  registered = true;
}

// Durum işareti derece skalası:
//   %100 üç yıldız (piramit diziliş) · %90-100 iki yıldız · %80-90 tek yıldız
//   %50-80 üçgen · %20-50 dolu daire · %20 ve altı boş daire
function statusMark(percent) {
  if (percent >= 100) return { rows: ["\u2605", "\u2605 \u2605"], color: GOLD };
  if (percent >= 90) return { rows: ["\u2605 \u2605"], color: GOLD };
  if (percent >= 80) return { rows: ["\u2605"], color: GOLD };
  if (percent >= 50) return { rows: ["\u25B2"], color: WARNING };
  if (percent > 20) return { rows: ["\u25CF"], color: DANGER };
  return { rows: ["\u25CB"], color: DANGER };
}

const BRAND = "#006A71";
const BRAND_SOFT = "#D6EBEB";
const INK = "#123B3D";
const MUTED = "#6C757D";
const BORDER = "#E2E8F0";
const WARNING = "#E8A23A";
const DANGER = "#C0392B";
const SUCCESS = "#1A5D1A";
const GOLD = "#E5A50A";

// İlerlemeye göre renk: düşük oran uyarı tonunda, hedef dolunca marka rengi.
function toneFor(percent) {
  if (percent >= 90) return BRAND;
  if (percent >= 80) return "#48A6A7";
  if (percent >= 50) return WARNING;
  return DANGER;
}

const styles = StyleSheet.create({
  page: { fontFamily: "Poppins", fontSize: 9, color: INK, paddingTop: 118, paddingBottom: 46 },
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 100,
    backgroundColor: BRAND,
    color: "#FFFFFF",
    paddingHorizontal: 32,
    paddingVertical: 20,
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
  },
  logo: { width: 30, height: 47 },
  headerTitle: { fontSize: 16, fontWeight: 700 },
  headerGroup: { fontSize: 11, marginTop: 4 },
  headerMeta: { fontSize: 8, marginTop: 8, color: "#DCEFEF" },
  body: { paddingHorizontal: 32 },
  summaryRow: { flexDirection: "row", gap: 10, marginBottom: 18 },
  summaryBox: { flex: 1, backgroundColor: BRAND_SOFT, borderRadius: 6, padding: 10 },
  summaryValue: { fontSize: 15, fontWeight: 700, color: BRAND },
  summaryLabel: { fontSize: 7.5, color: MUTED, marginTop: 2 },
  tableHead: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BRAND,
    paddingBottom: 5,
    marginBottom: 4,
  },
  th: { fontSize: 8, fontWeight: 600, color: BRAND },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  cell: { fontSize: 8.5 },
  badge: { width: 18, height: 18, marginRight: 5 },
  glyphBox: { width: 26, alignItems: "center", justifyContent: "center" },
  glyph: { fontFamily: "Symbols", fontSize: 7, lineHeight: 1, letterSpacing: 0.5 },
  countCell: { textAlign: "center", fontWeight: 600 },
  muted: { fontSize: 7.5, color: MUTED },
  barTrack: { height: 4, backgroundColor: BORDER, borderRadius: 2 },
  barTrackEmpty: {
    backgroundColor: "#FBEAE7",
    borderWidth: 0.7,
    borderColor: DANGER,
  },
  barFill: { height: 4, borderRadius: 2 },
  barValue: { fontSize: 8.5, fontWeight: 600, textAlign: "right", marginBottom: 2 },
  empty: { fontSize: 9, color: MUTED, paddingVertical: 8 },
  footer: {
    position: "absolute",
    bottom: 18,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: BORDER,
    paddingTop: 6,
  },
  footerText: { fontSize: 7.5, color: MUTED },
});

function StatusIcon({ percent }) {
  const safe = Math.max(0, Math.min(100, percent || 0));
  const mark = statusMark(safe);
  return (
    <View style={styles.glyphBox}>
      {mark.rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Text key={index} style={[styles.glyph, { color: mark.color }]}>
          {row}
        </Text>
      ))}
    </View>
  );
}

function Bar({ percent }) {
  const safe = Math.max(0, Math.min(100, percent || 0));
  return (
    <View>
      <Text style={[styles.barValue, { color: toneFor(safe) }]}>%{safe}</Text>
      <View style={[styles.barTrack, safe === 0 ? styles.barTrackEmpty : null]}>
        <View style={[styles.barFill, { width: `${safe}%`, backgroundColor: toneFor(safe) }]} />
      </View>
    </View>
  );
}

export default function GroupReportDocument({ model, labels, badges = {} }) {
  ensureFonts();
  const printedAt = new Date().toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <Document title={model.groupName} author={labels.appName}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Image src={amblem} style={styles.logo} />
          <View>
            <Text style={styles.headerTitle}>{labels.title}</Text>
            <Text style={styles.headerGroup} wrap={false}>
              {model.groupName}
            </Text>
            <Text style={styles.headerMeta}>
              {labels.rangeLabel}: {model.rangeLabel}   •   {labels.printedAt}: {printedAt}
            </Text>
          </View>
        </View>

        <View style={styles.body}>
          <View style={styles.summaryRow}>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryValue}>
                {model.summary.activeMembers}/{model.summary.memberCount}
              </Text>
              <Text style={styles.summaryLabel}>{labels.activeMembers}</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryValue}>{model.summary.totalActiveDays}</Text>
              <Text style={styles.summaryLabel}>{labels.activeDays}</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryValue}>%{model.summary.avgCompletion}</Text>
              <Text style={styles.summaryLabel}>{labels.avgCompletion}</Text>
            </View>
          </View>

          {model.labels.length === 0 ? (
            <Text style={styles.empty}>{labels.noData}</Text>
          ) : (
            <>
              <View style={styles.tableHead}>
                <Text style={[styles.th, { flex: 3 }]}>{labels.goal}</Text>
                <Text style={[styles.th, { flex: 1, textAlign: "center" }]}>{labels.value}</Text>
                <Text style={[styles.th, { flex: 2, paddingLeft: 20, textAlign: "right" }]}>
                  {labels.completion}
                </Text>
              </View>
              {model.labels.map((item) => (
                <View key={item.key || item.label} style={styles.row} wrap={false}>
                  <View style={{ flex: 3, flexDirection: "row", alignItems: "center" }}>
                    <StatusIcon percent={item.completion} />
                    <Text style={styles.cell}>{item.name || item.label}</Text>
                  </View>
                  <Text style={[styles.cell, { flex: 1, textAlign: "center" }]}>
                    {item.value} / {item.goalValue}
                  </Text>
                  <View style={{ flex: 2, paddingLeft: 20 }}>
                    <Bar percent={item.completion} />
                  </View>
                </View>
              ))}
            </>
          )}

          <View style={{ marginTop: 16 }} />
          {model.members.length === 0 ? (
            <Text style={styles.empty}>{labels.noData}</Text>
          ) : (
            <>
              <View style={styles.tableHead} fixed>
                <Text style={[styles.th, { flex: 3.6 }]}>{labels.member}</Text>
                <Text style={[styles.th, { flex: 1.3, textAlign: "center" }]}>
                  {labels.activeDaysShort}
                </Text>
                <Text style={[styles.th, { flex: 1.1, textAlign: "center" }]}>
                  {labels.perfectDays}
                </Text>
                <Text style={[styles.th, { flex: 1.3, textAlign: "center" }]}>{labels.total}</Text>
                <Text style={[styles.th, { flex: 1.9, paddingLeft: 20, textAlign: "right" }]}>
                  {labels.completion}
                </Text>
              </View>
              {model.members.map((member) => (
                <View key={member.id} style={styles.row} wrap={false}>
                  <View style={{ flex: 3.6, flexDirection: "row", alignItems: "center" }}>
                    <StatusIcon percent={member.completionRate} />
                    {member.badge && badges[member.badge.url] ? (
                      <Image src={badges[member.badge.url]} style={styles.badge} />
                    ) : null}
                    <View>
                      <Text style={styles.cell}>{member.name}</Text>
                      <Text style={styles.muted}>
                        {member.titleName ? `${member.titleName} · ${member.email}` : member.email}
                      </Text>
                    </View>
                  </View>
                  <Text
                    style={[
                      styles.cell,
                      styles.countCell,
                      { flex: 1.3, color: member.daysActiveCount > 0 ? SUCCESS : MUTED },
                    ]}
                  >
                    {member.daysActiveCount}
                  </Text>
                  <Text
                    style={[
                      styles.cell,
                      styles.countCell,
                      { flex: 1.1, color: member.perfectDaysCount > 0 ? BRAND : MUTED },
                    ]}
                  >
                    {member.perfectDaysCount}
                  </Text>
                  <Text style={[styles.cell, { flex: 1.3, textAlign: "center" }]}>
                    {member.totalValue} / {member.totalGoalValue}
                  </Text>
                  <View style={{ flex: 1.9, paddingLeft: 20 }}>
                    <Bar percent={member.completionRate} />
                  </View>
                </View>
              ))}
            </>
          )}
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            {labels.appName} — {model.groupName}
          </Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}
